import get from 'lodash/get'
import { N } from '@/scripts/constants/booleans'
import { CART_SAVED, CH_CART_SAVED } from '@/scripts/constants/cookies'
import { Cookies } from '@/scripts/libs/cookies'
import axios from 'axios'
import makeDebug from 'debug'
import qs from 'qs'
import { lockCriticalRequest, releaseCriticalRequest } from '../utilsAxios'
import { syncAjaxPOST } from '../apple-pay-helpers'
import wcs from '@/scripts/libs/wcs'
import { SHOPCART_DISPLAY_VIEW } from '@/scripts/constants/pages'
import { Url } from '../url'

const debug = makeDebug('wcs:cart')

interface WcsAddToBagParams {
  URL: string
  field1: number
  orderId: string
  pgName: string
  pageName: string
  calculateOrder: string
  calculationUsage: string
  quantity?: string
}

const addToBagParams: WcsAddToBagParams = {
  URL: SHOPCART_DISPLAY_VIEW,
  field1: 0,
  orderId: '.',
  pgName: 'PDP',
  pageName: 'ProductPage',
  calculateOrder: '1',
  calculationUsage: '-1,-3,-2,-4,-7',
  quantity: '1',
}

export class WcsCart {
  static getMinicart = async () => {
    const { catalogId, storeId, langId } = wcs.getStoreConfig()
    const addToCartUrl = wcs.getEndPoint('miniCart')
    const response = await axios.get(addToCartUrl, {
      params: {
        catalogId: catalogId,
        storeId: storeId,
        langId: langId,
      },
    })
    const data = response.data.JSONObject ? 'data.JSONObject' : 'data'
    const dataToSave = response.data.JSONObject ? response.data.JSONObject : response.data

    debug('wcs_get_minicart %o', response)

    Cookies.set(CART_SAVED, true)
    localStorage.setItem(CART_SAVED, JSON.stringify(dataToSave))
    localStorage.setItem(CH_CART_SAVED, JSON.stringify(dataToSave))
    try {
      if (wcs.isInCart()) {
        window.checkColoradoTax(dataToSave.orderDetails?.ColoradoTax)
      }
    } catch (err) {
      console.error('Error checking Colorado tax', err)
      return get(response, data, {})
    }

    return get(response, data, {})
  }

  static removeProduct = async ({
    orderItemId,
    orderId,
  }: {
    orderItemId: number
    orderId: number
  }) => {
    const { catalogId, storeId, langId } = wcs.getStoreConfig()
    const removeToCartUrl = wcs.getEndPoint('removeToCart')

    const response = await axios.get(removeToCartUrl, {
      params: {
        catalogId: catalogId,
        storeId: storeId,
        langId: langId,
        doInventory: N,
        quantity: 0,
        orderItemId,
        orderId,
        isDeleteFromCart: true,
      },
    })

    return response.data
  }

  // Need a synchronous version of this method, because of Apple Pay.
  // ApplePaySession can be triggered only with user click, therefore
  // callbacks in async actions are not user clicks.
  static addProductSync = ({ catEntry, parameters }: { catEntry: number; parameters: any }) => {
    const { catalogId, storeId, langId } = wcs.getStoreConfig()
    const addToCartUrl = wcs.getEndPoint('addToCart')

    const addToBagDefaultData = addToBagParams
    let params = null

    if (parameters) {
      // in case of rox products
      delete addToBagDefaultData['quantity']
      params = Url.makeParamsFromObject({
        ...addToBagDefaultData,
        catalogId: catalogId,
        storeId: storeId,
        langId: langId,
        ...parameters,
      })
    } else {
      params = Url.makeParamsFromObject({
        ...addToBagDefaultData,
        catalogId,
        storeId,
        langId,
        catEntryId: catEntry,
      })
    }

    const response = syncAjaxPOST(`${addToCartUrl}${params}`)

    if (typeof response !== 'object') {
      return JSON.parse(response)
    }

    return response
  }

  static addProduct = async ({
    catEntry,
    parameters,
    isPickupStore = false,
  }: {
    catEntry: number
    parameters: any
    isPickupStore: boolean
  }) => {
    const { catalogId, storeId, langId } = wcs.getStoreConfig()
    const addToCartUrl = wcs.getEndPoint('addToCart')
    let response = null
    const addToBagDefaultData = addToBagParams
    lockCriticalRequest(addToCartUrl)
    try {
      if (parameters) {
        // in case of rox products
        delete addToBagDefaultData['quantity']
        response = await axios.get(addToCartUrl, {
          params: {
            catalogId: catalogId,
            storeId: storeId,
            langId: langId,
            ...addToBagDefaultData,
            ...parameters,
          },
        })
      } else {
        const params = {
          ...addToBagDefaultData,
          catalogId: catalogId,
          storeId: storeId,
          langId: langId,
          catEntryId: catEntry,
        } as any
        if (isPickupStore) {
          params.isPickupStore = isPickupStore
        }
        response = await axios.get(addToCartUrl, {
          params,
        })
      }
    } finally {
      releaseCriticalRequest(addToCartUrl)
    }

    return response.data
  }

  static addRemixProduct = async ({ parameters }: any) => {
    const { catalogId, storeId, langId } = wcs.getStoreConfig()
    const addToCartUrl = '/AjaxDynamicKitOrderItemAdd'
    lockCriticalRequest(addToCartUrl)
    let response = null
    try {
      const params = {
        catalogId: catalogId,
        storeId: storeId,
        langId: langId,
        ...parameters,
      }
      response = await axios.post(addToCartUrl, qs.stringify(params))
    } finally {
      releaseCriticalRequest(addToCartUrl)
    }
    return response.data
  }

  static getPrice = (product: any, lens: any) => {
    if (lens) {
      if (lens.unformattedLensPromoPrice) {
        return (
          parseFloat(product.prices?.listPrice) + parseFloat(lens.unformattedLensPromoPrice)
        ).toFixed(2)
      } else {
        return (
          parseFloat(product.prices?.listPrice) + parseFloat(lens.unformattedLensPrice)
        ).toFixed(2)
      }
    } else {
      return parseFloat(product.prices?.listPrice).toFixed(2)
    }
  }

  static getFullPrice = (product: any, lens: any) => {
    if (lens) {
      return (
        parseFloat(product.prices?.listPrice) + parseFloat(lens.unformattedLensPrice)
      ).toFixed(2)
    } else {
      return parseFloat(product.prices?.listPrice).toFixed(2)
    }
  }

  static brazilCallStack = async (parameters: any) => {
    const firstCallURL = '/webapp/wcs/stores/servlet/RaybanCallICFreightAPIServiceCmd'
    const secondCallURL = wcs.getEndPoint('ajaxShipChargeUpdate')
    //start first call
    await axios
      .post(firstCallURL, qs.stringify(parameters))
      .then((response: any) => {
        const dataOptions = response.data.freightDetails.freightOptions[0]
        const params = {
          shipModeId: dataOptions.shipModeId,
          brazilShippingDays: dataOptions.shipmentTime,
          orderId: parameters.orderId,
          brazilZipCode: parameters.postalCode,
        }
        //start second call
        axios.post(secondCallURL, qs.stringify(params))
      })
      .catch((error: any) => {
        throw error
      })
  }
}
